import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import client from "@culturehq/client";
import { Panel, Subnav, Thumbnail } from "@culturehq/components";

import { useFetch, useLeaderboardUpdateListener } from "app/hooks";
import { ProfileLink } from "app/links";
import { t, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import getUserName from "app/utils/getUserName";

const LeaderboardRow = ({ monthPoints, place, user, isMe }) => (
  <div role="row" className={`row leaderboard--row ${isMe ? "isMe" : ""}`}>
    <div className="col-xs-9">
      <div role="cell" className="leaderboard--place">
        {place < 100 && <span>{place}</span>}
      </div>
      <div role="cell" className="leaderboard--user">
        <ProfileLink user={user}>
          <Thumbnail className="leaderboard--thumb" image={user.avatar.thumbUrl} />
          {isMe ? "Me" : getUserName(user)}
        </ProfileLink>
      </div>
    </div>
    <div role="cell" className="col-xs-3 leaderboard--points">{typeof monthPoints === "number" ? monthPoints : user.points}</div>
  </div>
);

const Leaderboard = () => {
  const { error, fetching, json, onUpdate } = useFetch(client.getPointLeaderboard);
  const {
    error: errorMonth, fetching: fetchingMonth, json: jsonMonth, onUpdate: onUpdateMonth
  } = useFetch(
    () => client.getPointLeaderboard({ currentMonth: true }),
    []
  );
  const leaderboard = json.pointLeaderboard ? json.pointLeaderboard.leaderboard.slice(0, 25) : [];
  const leaderboardMonth = jsonMonth.pointLeaderboard
    ? jsonMonth.pointLeaderboard.leaderboard.slice(0, 25) : [];

  // TODO: check on update month leaderboard
  useLeaderboardUpdateListener(
    useCallback(
      async ({ pointLeaderboard: { leaderboard: updatedLeaderboard } }) => {
        const { pointStanding: { standing } } = await client.getProfilePointStanding();

        onUpdate(current => {
          const currentUser = current.pointLeaderboard.leaderboard[0].user;
          const nextLeaderboard = [
            { ranking: standing, user: currentUser },
            ...updatedLeaderboard.filter(({ user: { id } }) => id !== currentUser.id).slice(0, 5)
          ];

          return { ...current, pointLeaderboard: { leaderboard: nextLeaderboard } };
        });
      },
      [onUpdate]
    )
  );

  const [isVisibleAlltime, setIsVisibleAlltime] = useState(false);

  const handleChange = index => {
    if (index === 0) {
      setIsVisibleAlltime(false);
    } else {
      setIsVisibleAlltime(true);
    }
  };

  return (
    <Panel className="leaderboard SidePanel">
      <Panel.Heading>
        <Trans>Top Community Scores</Trans>
      </Panel.Heading>
      <Panel.LoaderBody loading={fetching || fetchingMonth}>
        <div className="top-scores">
          <Subnav ariaLabel={t("Top Community Scores")} onChange={handleChange}>
            <Subnav.Item>Monthly</Subnav.Item>
            <Subnav.Item>All-Time</Subnav.Item>
          </Subnav>
        </div>
        <ErrorBoundary>
          {isVisibleAlltime ? (
            <>
              {error && <ErrorMessage error={error} />}
              <div
                className="leaderboard-container no-scrollbar"
                role="table"
                aria-label={t("Top Community Scores")}
              >
                <div role="rowgroup">
                  {leaderboard.map((entry, index) => (
                    <LeaderboardRow
                      key={index}
                      place={entry.ranking}
                      user={entry.user}
                      isMe={index === 0}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {errorMonth && <ErrorMessage error={errorMonth} />}
              <div
                className="leaderboard-container no-scrollbar"
                role="table"
                aria-label={t("Top Community Scores")}
              >
                <div role="rowgroup">
                  {leaderboardMonth.map((entry, index) => (
                    <LeaderboardRow
                      key={index}
                      monthPoints={entry.pointsThisMonth}
                      place={entry.ranking}
                      user={entry.user}
                      isMe={index === 0}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </ErrorBoundary>
      </Panel.LoaderBody>
      <Panel.Footer>
        <Link to="/score">
          <Trans>What is a Community Score?</Trans>
        </Link>
      </Panel.Footer>
    </Panel>
  );
};

export default Leaderboard;
